import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { occupationApi } from '@/services/skillKnowledgeApi';
import { useEntity, useMutation } from '@/hooks';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, Edit, Trash2 } from 'lucide-react';
import { LoadingState, ErrorState, CopyableId } from '@/components/common';
import type { Occupation } from '@/types/skillKnowledge';

export const OccupationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAdminAuth();

  const canEdit = hasPermission('edit', 'occupation');
  const canDelete = hasPermission('delete', 'occupation');

  // Use entity hook for fetching occupation
  const {
    data: occupation,
    isLoading,
    error,
    refetch,
  } = useEntity<Occupation>({
    fetchFn: (occupationId: string) => occupationApi.getById(occupationId),
    entityId: id,
    entityName: 'occupation',
    enabled: !!id,
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('Occupation ID is required');
      await occupationApi.delete(id);
    },
    entityName: 'occupation',
    operation: 'delete',
    onSuccess: () => {
      navigate('/admin/occupations');
    },
  });

  const handleDelete = async () => {
    if (!id || !window.confirm('Are you sure you want to delete this occupation?')) return;
    await deleteMutation.mutate();
  };

  if (isLoading) {
    return <LoadingState message="Loading occupation..." />;
  }

  if (error || !occupation) {
    return <ErrorState error={error || new Error('Occupation not found')} onRetry={refetch} />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/occupations">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{occupation.title}</h1>
            <p className="text-muted-foreground mt-1">Occupation Details</p>
          </div>
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <Button asChild>
              <Link to={`/admin/occupations/${id}/edit`}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </Button>
          )}
          {canDelete && (
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isLoading}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
            <CardDescription>Core details about this occupation</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <CopyableId id={occupation.id || id || ''} label="Occupation ID" />
            <div>
              <label className="text-sm font-medium text-muted-foreground">Title</label>
              <p className="mt-1 font-medium">{occupation.title}</p>
            </div>
            {occupation.alternativeTitles && occupation.alternativeTitles.length > 0 && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Alternative Titles</label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {occupation.alternativeTitles.map((title, idx) => (
                    <span key={idx} className="px-2 py-1 bg-secondary text-secondary-foreground rounded text-sm">
                      {title}
                    </span>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-muted-foreground">Description</label>
              <p className="mt-1 text-sm">{occupation.description}</p>
            </div>
            {occupation.summary && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Summary</label>
                <p className="mt-1 text-sm">{occupation.summary}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Classification */}
        <Card>
          <CardHeader>
            <CardTitle>Classification</CardTitle>
            <CardDescription>Category and type information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {occupation.category && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Category</label>
                <p className="mt-1">{occupation.category}</p>
              </div>
            )}
            {occupation.industry && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Industry</label>
                <p className="mt-1">{occupation.industry}</p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-muted-foreground">Career Level</label>
              <p className="mt-1">
                <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded text-sm">
                  {occupation.careerLevel?.replace(/_/g, ' ')}
                </span>
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Occupation Type</label>
              <p className="mt-1">
                <span className="px-2 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded text-sm">
                  {occupation.occupationType?.replace(/_/g, ' ')}
                </span>
              </p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Status</label>
              <p className="mt-1">
                <span
                  className={`px-2 py-1 rounded text-sm ${
                    occupation.status === 'ACTIVE'
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : occupation.status === 'DRAFT'
                      ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                  }`}
                >
                  {occupation.status || 'N/A'}
                </span>
              </p>
            </div>
            {occupation.growthOutlook && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Growth Outlook</label>
                <p className="mt-1">
                  <span
                    className={`px-2 py-1 rounded text-sm ${
                      occupation.growthOutlook === 'RAPIDLY_GROWING' || occupation.growthOutlook === 'GROWING'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : occupation.growthOutlook === 'STABLE'
                        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                        : occupation.growthOutlook === 'EMERGING'
                        ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
                        : 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                    }`}
                  >
                    {occupation.growthOutlook.replace(/_/g, ' ')}
                  </span>
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Requirements */}
        <Card>
          <CardHeader>
            <CardTitle>Requirements</CardTitle>
            <CardDescription>Experience and education requirements</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {occupation.experienceRequired !== undefined && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Experience Required</label>
                <p className="mt-1">{occupation.experienceRequired} years</p>
              </div>
            )}
            {occupation.educationRequired && occupation.educationRequired.length > 0 && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Education Required</label>
                <ul className="mt-1 list-disc list-inside space-y-1">
                  {occupation.educationRequired.map((edu, idx) => (
                    <li key={idx} className="text-sm">{edu}</li>
                  ))}
                </ul>
              </div>
            )}
            {occupation.popularityScore !== undefined && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Popularity Score</label>
                <p className="mt-1">{occupation.popularityScore}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Audit Information */}
        <Card>
          <CardHeader>
            <CardTitle>Audit Information</CardTitle>
            <CardDescription>Creation and update history</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Created At</label>
                <p className="mt-1 text-sm">
                  {occupation.createdAt
                    ? new Date(occupation.createdAt).toLocaleString()
                    : 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Updated At</label>
                <p className="mt-1 text-sm">
                  {occupation.updatedAt
                    ? new Date(occupation.updatedAt).toLocaleString()
                    : 'N/A'}
                </p>
              </div>
              {occupation.createdBy && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Created By</label>
                  <p className="mt-1 text-sm">{occupation.createdBy}</p>
                </div>
              )}
              {occupation.updatedBy && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Updated By</label>
                  <p className="mt-1 text-sm">{occupation.updatedBy}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Required Skills */}
      {occupation.requiredSkills && occupation.requiredSkills.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Required Skills ({occupation.requiredSkills.length})</CardTitle>
            <CardDescription>Skills needed for this occupation</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {occupation.requiredSkills.map((skill, idx) => (
                <div key={idx} className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <Link
                      to={`/admin/skills/${skill.skillId}`}
                      className="font-medium text-blue-600 hover:underline"
                    >
                      {skill.skillName || 'View Skill'}
                    </Link>
                    {skill.isCritical && (
                      <span className="px-2 py-0.5 bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 rounded text-xs">
                        Critical
                      </span>
                    )}
                  </div>
                  <CopyableId id={skill.skillId} label="Skill ID" />
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>Level: {skill.proficiencyLevel}</span>
                    {skill.importance !== undefined && (
                      <span>| Importance: {skill.importance}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Optional Skills */}
      {occupation.optionalSkills && occupation.optionalSkills.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Optional Skills ({occupation.optionalSkills.length})</CardTitle>
            <CardDescription>Additional skills that are beneficial</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {occupation.optionalSkills.map((skill, idx) => (
                <div key={idx} className="border rounded-lg p-4 space-y-3">
                  <Link
                    to={`/admin/skills/${skill.skillId}`}
                    className="font-medium text-blue-600 hover:underline"
                  >
                    {skill.skillName || 'View Skill'}
                  </Link>
                  <CopyableId id={skill.skillId} label="Skill ID" />
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>Level: {skill.proficiencyLevel}</span>
                    {skill.importance !== undefined && (
                      <span>| Importance: {skill.importance}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default OccupationDetailPage;
